/*
 * pdf-text-font.js — the text-into-PDF font core for /pdf-editor.
 *
 * THE PROBLEM IT SOLVES
 * ─────────────────────
 * Putting user text into a PDF fails in two ways, and the dangerous one is
 * silent. Both were measured on 2026-08-10, not assumed:
 *
 *   1. pdf-lib's StandardFonts are WinAnsi. `drawText('Привет')` THROWS
 *      `WinAnsi cannot encode "П" (0x041f)`. Loud — you cannot ship this
 *      by accident.
 *
 *   2. An embedded TTF missing the glyph draws .notdef (a blank box), and
 *      `font.widthOfTextAtSize()` STILL RETURNS A PLAUSIBLE NUMBER.
 *      NotoSans-Regular reported widths for Korean, Arabic, Chinese and
 *      emoji and has glyphs for none of them. Silent — this is how every
 *      "why is my Russian text blank" bug in an online PDF editor happens.
 *
 * Because of (2), coverage is never inferred from a width, a font name, or
 * the UI locale. It is asked of the font's cmap: cheaply here (a frozen
 * range table built by scripts/build-font-coverage.mjs) and authoritatively
 * at embed time (fontkit, which is loaded anyway to embed the face).
 *
 * WHAT IT DOES THAT THE COMPETITION DOESN'T
 * ─────────────────────────────────────────
 * `planRuns()` splits a mixed-script string into runs and gives each run a
 * face that can actually render it. "Договор 계약서" becomes two runs, two
 * fonts, one line — instead of one font and a row of blank boxes. No online
 * PDF editor surveyed on 2026-08-10 does this.
 *
 * Subsetting is on by default: measured 295.6 KB → 8.1 KB for one line of
 * Cyrillic. Never embed a full face into a user's document.
 */
/* The global is `self` in a Worker and `window` on a page. Testing only for
   `window` — as this did until 2026-08-10 — makes importScripts() "succeed"
   in a worker while attaching the API to nothing, so the caller sees an
   undefined module and no error anywhere. /add-watermark runs pdf-lib off the
   main thread, which is exactly where it mattered. */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.PdfTextFont = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var COVERAGE_URL     = '/data/font-coverage.json';
    var COVERAGE_CJK_URL = '/data/font-coverage-cjk.json';
    var FONTKIT_URL      = '/vendor/fontkit/fontkit.umd.min.js';

    var _table = null;          // { faces: [...] }
    var _cjkLoaded = false;
    var _tablePromise = null;
    var _ttfCache = {};         // url → Promise<Uint8Array>
    var _fontkitPromise = null;

    /* ── coverage lookup ────────────────────────────────────────────────── */

    /* Ranges are sorted, non-overlapping, and stored as [start] or [start,end].
       Binary search keeps a 4000-range CJK face as cheap as a 20-range one. */
    function inRanges(ranges, cp) {
        var lo = 0, hi = ranges.length - 1;
        while (lo <= hi) {
            var mid = (lo + hi) >> 1;
            var r = ranges[mid];
            var start = r[0];
            var end = r.length > 1 ? r[1] : r[0];
            if (cp < start) hi = mid - 1;
            else if (cp > end) lo = mid + 1;
            else return true;
        }
        return false;
    }

    /* Codepoints that carry no glyph of their own and must never be counted as
       "missing" — a face without them still renders the string correctly. */
    function isIgnorable(cp) {
        return cp === 0x20 || cp === 0x09 || cp === 0x0a || cp === 0x0d ||
               cp === 0xa0 ||                       // nbsp
               cp === 0x200b || cp === 0x200c || cp === 0x200d ||  // zw space/nj/j
               cp === 0xfeff ||                     // BOM
               (cp >= 0x202a && cp <= 0x202e) ||    // bidi embedding
               (cp >= 0x2066 && cp <= 0x2069);      // bidi isolates
    }

    /* Iterate real codepoints (surrogate pairs as one), skipping ignorables. */
    function codepoints(text) {
        var out = [];
        for (var i = 0; i < text.length; i++) {
            var cp = text.codePointAt(i);
            if (cp > 0xffff) i++;
            if (!isIgnorable(cp)) out.push(cp);
        }
        return out;
    }

    function faceById(id) {
        if (!_table) return null;
        for (var i = 0; i < _table.faces.length; i++) {
            if (_table.faces[i].id === id) return _table.faces[i];
        }
        return null;
    }

    /* A deferred face (CJK) has no ranges until the side table is fetched.
       Treating "no ranges" as "covers nothing" would hide the only faces that
       can render CJK, so it reports `null` = unknown, and callers that care
       must `loadCjk()` first. */
    function covers(face, cp) {
        if (!face) return false;
        if (!face.ranges) return null;
        return inRanges(face.ranges, cp);
    }

    /* ── public: coverage questions ─────────────────────────────────────── */

    /* Characters `faceId` cannot render. Empty array = safe to use.
       Unknown-coverage faces report [] here and are settled at embed time. */
    function missing(text, faceId) {
        var face = faceById(faceId);
        if (!face || !face.ranges) return [];
        var seen = {}, out = [];
        var cps = codepoints(String(text || ''));
        for (var i = 0; i < cps.length; i++) {
            var cp = cps[i];
            if (seen[cp]) continue;
            seen[cp] = 1;
            if (!inRanges(face.ranges, cp)) out.push(String.fromCodePoint(cp));
        }
        return out;
    }

    function canRender(text, faceId) {
        return missing(text, faceId).length === 0;
    }

    /* Every face that can render the whole string, in picker order. */
    function facesFor(text) {
        if (!_table) return [];
        var out = [];
        for (var i = 0; i < _table.faces.length; i++) {
            var f = _table.faces[i];
            if (!f.ranges) continue;               // unknown → not claimed
            if (canRender(text, f.id)) out.push(f);
        }
        return out;
    }

    /* Best face for `text`: the caller's preference when it works, otherwise
       the first shipped face that covers the string. Returns null when nothing
       we ship can render it — an honest null, never a silent tofu fallback. */
    function pick(text, preferId) {
        if (preferId && canRender(text, preferId) && faceById(preferId)) return preferId;
        var list = facesFor(text);
        return list.length ? list[0].id : null;
    }

    /* Split mixed-script text into runs, each with a face that covers it.
       Adjacent characters stay in one run while the current face still works,
       so "Договор 계약서 2026" yields the fewest possible font switches.
       Characters nothing covers are returned in a run with faceId === null;
       the caller decides whether to warn, drop, or rasterize them. */
    function planRuns(text, preferId) {
        var s = String(text || '');
        if (!s) return [];
        if (!_table) return [{ text: s, faceId: preferId || null }];

        var runs = [];
        var cur = null;

        for (var i = 0; i < s.length; i++) {
            var cp = s.codePointAt(i);
            var ch = String.fromCodePoint(cp);
            if (cp > 0xffff) i++;

            var faceId;
            if (isIgnorable(cp)) {
                // Whitespace joins whatever run is open rather than forcing a
                // switch — otherwise every space would split the line.
                faceId = cur ? cur.faceId : (preferId || null);
            } else if (cur && cur.faceId && covers(faceById(cur.faceId), cp) === true) {
                faceId = cur.faceId;               // current face still works
            } else {
                faceId = pick(ch, preferId);       // find one that does
            }

            if (cur && cur.faceId === faceId) cur.text += ch;
            else { cur = { text: ch, faceId: faceId }; runs.push(cur); }
        }
        return runs;
    }

    /* ── loading ────────────────────────────────────────────────────────── */

    /* ── requests that always answer (01.10.2026) ─────────────────────────
       A font request that neither answers nor fails — a stalled connection —
       held every PDF export built on this core for good. /business-card-maker
       sat 61–690 s in «exporting» (tier-0, 23–25.09), the shape of refund #37
       on /stamp-maker. Each request has a SILENCE watchdog: no bytes for
       SILENCE_MS (30 s of visible time on a page — a hidden tab is not a stall; plain
       time in a worker) and it is aborted and asked once more on a fresh URL;
       a second silence is a typed failure (code 'WAIT_TIMEOUT', phase
       'font_file') the tool turns into a message. Silence, not a deadline on
       the whole file: a 10 MB CJK face on a slow link keeps arriving and is
       not cut. And a failure is not cached — the old code kept a rejected
       coverage table and fontkit promise for the life of the page, so one
       network blip killed every later export until reload.
       Guard: tests/js/pdf-text-font-stall.test.mjs. */
    var SILENCE_MS = 30000;
    var SCRIPT_MS = 45000;

    function scope() {
        return (typeof self !== 'undefined') ? self : (typeof window !== 'undefined' ? window : null);
    }
    function after(fn, ms) {
        var g = scope();
        if (g && typeof g.__visibleTimeout === 'function') return g.__visibleTimeout(fn, ms);
        var h = setTimeout(fn, ms);
        return function () { clearTimeout(h); };
    }
    function silenceError(what, phase, ms) {
        var e = new Error('went silent: ' + what);
        e.name = 'WaitTimeoutError'; e.code = 'WAIT_TIMEOUT'; e.phase = phase; e.ms = ms;
        return e;
    }
    function fetchOnce(url) {
        var ac = typeof AbortController === 'function' ? new AbortController() : null;
        var cancel = null, silent = false;
        function arm() {
            if (cancel) cancel();
            cancel = after(function () { silent = true; if (ac) ac.abort(); }, SILENCE_MS);
        }
        arm();
        return fetch(url, { credentials: 'same-origin', signal: ac ? ac.signal : undefined }).then(function (r) {
            if (!r.ok) throw new Error('font ' + r.status + ' ' + url);
            if (!r.body || typeof r.body.getReader !== 'function') return r.arrayBuffer();
            var reader = r.body.getReader(), parts = [], n = 0;
            function pump() {
                return reader.read().then(function (x) {
                    if (x.done) return null;
                    arm();
                    parts.push(x.value); n += x.value.byteLength;
                    return pump();
                });
            }
            return pump().then(function () {
                var out = new Uint8Array(n), o = 0;
                for (var i = 0; i < parts.length; i++) { out.set(parts[i], o); o += parts[i].byteLength; }
                return out.buffer;
            });
        }).then(function (buf) {
            if (cancel) cancel();
            return buf;
        }, function (e) {
            if (cancel) cancel();
            throw silent ? silenceError(url, 'font_file', SILENCE_MS) : e;
        });
    }
    function fetchBytes(url) {
        return fetchOnce(url).catch(function (e) {
            if (!e || e.code !== 'WAIT_TIMEOUT') throw e;
            return fetchOnce(url + (url.indexOf('?') >= 0 ? '&' : '?') + 'r=' + Date.now());
        });
    }
    function fetchJson(url) {
        return fetchBytes(url).then(function (buf) { return JSON.parse(new TextDecoder().decode(buf)); });
    }

    function load() {
        if (_tablePromise) return _tablePromise;
        _tablePromise = fetchJson(COVERAGE_URL)
            .then(function (t) { _table = t; return t; })
            .catch(function (e) { _tablePromise = null; throw e; });
        return _tablePromise;
    }

    /* Pull the CJK ranges in. Only worth calling once CJK text appears. */
    function loadCjk() {
        if (_cjkLoaded) return Promise.resolve(_table);
        return load().then(function () {
            return fetchJson(COVERAGE_CJK_URL);
        }).then(function (d) {
            for (var i = 0; i < _table.faces.length; i++) {
                var f = _table.faces[i];
                if (d.faces[f.id]) { f.ranges = d.faces[f.id]; delete f.deferred; }
            }
            _cjkLoaded = true;
            return _table;
        });
    }

    /* Does this string need the deferred (CJK) tables to be judged fairly? */
    function needsCjk(text) {
        return /[⺀-鿿가-힯豈-﫿ｦ-ﾟ]/.test(String(text || ''));
    }

    /* Ensure coverage data adequate for `text` is present — and, when the
       caller names the face it is about to draw with, adequate for THAT face.

       ⚠️⚠️ A deferred face has no ranges until the CJK side table arrives, and
       `missing()` reports "unknown" as []. So `canRender('СТОП', 'cjk')` said
       TRUE while the table was absent: the text is not CJK, `needsCjk` is
       false, nobody fetched the table, and the chosen Noto Sans SC outlined
       four .notdef boxes with no warning at all. Measured 12.09.2026 on
       /stencil-maker: 3 of the 21 offered faces (exactly korean/cjk/japanese)
       drew empty frames for Cyrillic; after `loadCjk()` the same call answers
       false and `pick` returns noto. The question "can THIS face render it"
       needs this face's table, whatever script the text is in. */
    function ready(text, faceId) {
        return load().then(function () {
            if (_cjkLoaded) return _table;
            var f = faceId ? faceById(faceId) : null;
            return (needsCjk(text) || (f && !f.ranges)) ? loadCjk() : _table;
        });
    }

    /* Works on a page AND inside a Worker: /add-watermark and friends run
       pdf-lib off the main thread, and a font core that only knows how to
       append a <script> would be unusable exactly where the crash lives. */
    function loadFontkit() {
        var g = (typeof self !== 'undefined') ? self : (typeof window !== 'undefined' ? window : null);
        if (!g) return Promise.reject(new Error('no global scope'));
        if (g.fontkit) return Promise.resolve(g.fontkit);
        if (_fontkitPromise) return _fontkitPromise;

        // No CDN fallback on purpose: a CDN that is blocked in one country
        // turns a working tool into a broken one, and the pdf.js wave of
        // 2026-08-07 already removed exactly this pattern from 10 blades.
        if (typeof importScripts === 'function') {           // Worker
            _fontkitPromise = new Promise(function (resolve, reject) {
                try {
                    importScripts(FONTKIT_URL);
                    if (g.fontkit) resolve(g.fontkit);
                    else reject(new Error('fontkit loaded but absent'));
                } catch (e) {
                    reject(new Error('fontkit failed to load: ' + e.message));
                }
            });
            return _fontkitPromise;
        }

        /* page: the shared asset chain when the page has it (bounded attempts,
           a retry, the stall reported), else the same rule here — a tag that
           neither loads nor fails is abandoned after SCRIPT_MS and asked once
           more on a fresh URL. A failure is not cached. */
        var tag = function (src) {
            return new Promise(function (resolve, reject) {
                var s = document.createElement('script');
                var cancel = after(function () { reject(silenceError(src, 'script', SCRIPT_MS)); }, SCRIPT_MS);
                s.src = src;
                s.onload = function () { cancel(); resolve(); };
                s.onerror = function () { cancel(); s.remove(); reject(new Error('fontkit failed to load')); };
                document.head.appendChild(s);
            });
        };
        var chain = g.__assetChain && g.__assetChain.loadScript;
        var p = chain
            ? g.__assetChain.loadScript([FONTKIT_URL], 'fontkit')
            : tag(FONTKIT_URL).catch(function () { return tag(FONTKIT_URL + '?retry=' + Date.now()); });
        _fontkitPromise = p.then(function () {
            if (g.fontkit) return g.fontkit;
            throw new Error('fontkit loaded but absent');
        }).catch(function (e) {
            _fontkitPromise = null;
            if (e && !e.phase) e.phase = 'script';
            throw e;
        });
        return _fontkitPromise;
    }

    function fetchTtf(url) {
        if (_ttfCache[url]) return _ttfCache[url];
        _ttfCache[url] = fetchBytes(url)
            .then(function (b) {
                var raw = new Uint8Array(b);
                /* A face we fail to understand must still load. Every branch in
                   alignGlyphData already bails out to the original bytes, but a
                   throw here would reject the font promise and take the whole
                   tool down over a cosmetic repair. */
                try { return alignGlyphData(raw); } catch (e) { return raw; }
            })
            .catch(function (e) { delete _ttfCache[url]; throw e; });
        return _ttfCache[url];
    }

    /* ── glyph alignment: the subsetter's half-byte ──────────────────────── */

    /* fontkit picks the SHORT `loca` format for a subset by SIZE ALONE, and a
       short `loca` stores every offset DIVIDED BY TWO:

           loca.preEncode = function () {
               this.version = this.offsets[this.offsets.length - 1] > 0xffff ? 1 : 0;
               if (this.version === 0) for (...) this.offsets[i] >>>= 1;
           }

       A subset always fits in 64 KB, so it is always the short format. Nothing
       pads the glyph data, so a glyph whose stored length is ODD loses half a
       byte and shifts every glyph after it. The reader then parses the next
       glyph's header out of the middle of the previous glyph's coordinates.
       What comes back is `numberOfContours = 5888`, and the page draws garbage
       or — far more often — nothing at all, with no error anywhere.

       Measured 2026-08-20, one plain string per face, counting glyphs whose
       header is nonsense after subsetting:

           NotoSansKR      "홍길동 상품명"     3 of 7 broken
           NotoSansSC      "张伟 产品名称"     3 of 7
           NotoSansJP      "山田太郎 商品名"   2 of 8
           NotoSansArabic  "محمد أحمد"        3 of 8
           Caveat-Bold     "Иванов Пётр"      6 of 16
           NotoSans / Montserrat / IBMPlexSansArabic — clean

       The clean ones are not better fonts; they are fonts whose glyph data is
       already even-length (Montserrat ships a short `loca`, so it had to be).
       About half of every CJK face's glyphs are odd — which is why "the Korean
       PDF came out blank" is not an edge case there.

       We cannot patch the dependency, and a build step is no good either: the
       vendor font directory is gitignored and populated by download.sh on each
       origin, so any font that arrives later would miss the fix. Making the
       INPUT safe covers every face, including ones added tomorrow: pad each
       glyph up to an EVEN length and rewrite `loca` long. Even, not the
       4 bytes the spec recommends — evenness is the whole of what `>>> 1`
       needs, and padding further would rewrite fonts that were never broken
       (Montserrat: +2.14 % for nothing). Only zero bytes are added, between
       glyphs — outlines are untouched, so the same face renders as before.

       One pass at load, once per URL (the result is what `_ttfCache` holds),
       about 0.7 % more bytes. An already-aligned font is returned as-is. */

    function u16(b, p) { return (b[p] << 8) | b[p + 1]; }
    function u32(b, p) { return ((b[p] << 24) >>> 0) + (b[p + 1] << 16) + (b[p + 2] << 8) + b[p + 3]; }
    function put16(b, p, v) { b[p] = (v >> 8) & 0xff; b[p + 1] = v & 0xff; }
    function put32(b, p, v) { b[p] = (v >>> 24) & 0xff; b[p + 1] = (v >>> 16) & 0xff; b[p + 2] = (v >>> 8) & 0xff; b[p + 3] = v & 0xff; }

    /* Big-endian uint32 sum, zero-padded — the OpenType table checksum. Kept
       correct so the bytes stay a valid font if anything stricter than pdf-lib
       ever sees them. */
    function sfntChecksum(b, off, len) {
        var sum = 0;
        for (var i = 0; i < len; i += 4) {
            sum = (sum + (((b[off + i] || 0) << 24) >>> 0) + ((b[off + i + 1] || 0) << 16)
                       + ((b[off + i + 2] || 0) << 8) + (b[off + i + 3] || 0)) >>> 0;
        }
        return sum;
    }

    function alignGlyphData(bytes) {
        if (!bytes || bytes.length < 12) return bytes;
        var ver = u32(bytes, 0);
        /* 0x00010000 and "true" are TrueType outlines. OTTO (CFF) has no
           glyf/loca and cannot hit this; a collection is not something we ship. */
        if (ver !== 0x00010000 && ver !== 0x74727565) return bytes;

        var numTables = u16(bytes, 4), i, p, tag;
        var dir = [], byTag = {};
        for (i = 0; i < numTables; i++) {
            p = 12 + i * 16;
            if (p + 16 > bytes.length) return bytes;
            tag = String.fromCharCode(bytes[p], bytes[p + 1], bytes[p + 2], bytes[p + 3]);
            var e = { tag: tag, off: u32(bytes, p + 8), len: u32(bytes, p + 12) };
            dir.push(e); byTag[tag] = e;
        }
        var head = byTag.head, loca = byTag.loca, glyf = byTag.glyf;
        if (!head || !loca || !glyf) return bytes;              // CFF, or stripped
        if (head.off + 54 > bytes.length) return bytes;

        var longLoca = u16(bytes, head.off + 50) === 1;
        var n = longLoca ? (loca.len >>> 2) : (loca.len >>> 1);
        if (n < 2) return bytes;

        var offs = new Array(n);
        for (i = 0; i < n; i++) {
            offs[i] = longLoca ? u32(bytes, loca.off + i * 4) : u16(bytes, loca.off + i * 2) * 2;
        }
        /* Trust nothing: a non-monotonic loca means we do not understand this
           file, and guessing would be worse than leaving it alone. */
        for (i = 1; i < n; i++) if (offs[i] < offs[i - 1]) return bytes;
        if (glyf.off + offs[n - 1] > bytes.length) return bytes;

        var needs = false;
        for (i = 0; i + 1 < n; i++) if ((offs[i + 1] - offs[i]) & 1) { needs = true; break; }
        if (!needs) return bytes;

        /* Empty glyphs stay empty: a zero-length glyph is legal, and padding it
           would invent an outline slot the font never had. */
        var newOffs = new Array(n), total = 0, len;
        for (i = 0; i + 1 < n; i++) {
            newOffs[i] = total;
            len = offs[i + 1] - offs[i];
            total += len ? ((len + 1) & ~1) : 0;
        }
        newOffs[n - 1] = total;

        var newGlyf = new Uint8Array(total);
        for (i = 0; i + 1 < n; i++) {
            len = offs[i + 1] - offs[i];
            if (len) newGlyf.set(bytes.subarray(glyf.off + offs[i], glyf.off + offs[i] + len), newOffs[i]);
        }
        var newLoca = new Uint8Array(n * 4);
        for (i = 0; i < n; i++) put32(newLoca, i * 4, newOffs[i]);

        var out = [], size = 12 + numTables * 16;
        for (i = 0; i < numTables; i++) {
            var d = dir[i], data;
            if (d.tag === 'glyf') data = newGlyf;
            else if (d.tag === 'loca') data = newLoca;
            else data = bytes.subarray(d.off, d.off + d.len);
            out.push({ tag: d.tag, data: data });
            size += (data.length + 3) & ~3;
        }
        var buf = new Uint8Array(size);
        buf.set(bytes.subarray(0, 12), 0);
        var pos = 12 + numTables * 16, headPos = -1;
        for (i = 0; i < numTables; i++) {
            var t = out[i], dp = 12 + i * 16;
            buf[dp] = t.tag.charCodeAt(0); buf[dp + 1] = t.tag.charCodeAt(1);
            buf[dp + 2] = t.tag.charCodeAt(2); buf[dp + 3] = t.tag.charCodeAt(3);
            buf.set(t.data, pos);
            if (t.tag === 'head') {
                headPos = pos;
                put16(buf, pos + 50, 1);          // indexToLocFormat → long
                put32(buf, pos + 8, 0);           // checkSumAdjustment zeroed for the file sum
            }
            put32(buf, dp + 8, pos);
            put32(buf, dp + 12, t.data.length);
            pos += (t.data.length + 3) & ~3;
        }
        for (i = 0; i < numTables; i++) {
            var dq = 12 + i * 16;
            put32(buf, dq + 4, sfntChecksum(buf, u32(buf, dq + 8), u32(buf, dq + 12)));
        }
        if (headPos >= 0) put32(buf, headPos + 8, (0xB1B0AFBA - sfntChecksum(buf, 0, buf.length)) >>> 0);
        return buf;
    }

    /* ── embedding ──────────────────────────────────────────────────────── */

    /* Embed `faceId` into a pdf-lib document and hand back the font object.
       Registers fontkit on the doc once. Subsets by default — a full face is
       ~300 KB of document weight for a single line of text.

       Set `opts.bold` to prefer the face's bold file when it ships one. */
    function embed(pdfDoc, faceId, opts) {
        opts = opts || {};
        var face = faceById(faceId);
        if (!face) return Promise.reject(new Error('unknown face: ' + faceId));
        var url = (opts.bold && face.boldUrl) ? face.boldUrl : face.url;

        return Promise.all([loadFontkit(), fetchTtf(url)]).then(function (res) {
            var fk = res[0], bytes = res[1];
            if (!pdfDoc.__fontkitRegistered) {
                pdfDoc.registerFontkit(fk);
                pdfDoc.__fontkitRegistered = true;
            }
            return pdfDoc.embedFont(bytes, {
                subset: opts.subset !== false,
                // Custom names keep two faces from colliding in the resource
                // dictionary when a line uses several.
                customName: 'TMB' + faceId + (opts.bold ? 'B' : ''),
            });
        });
    }

    /* ── text as outlines ("шрифты в кривые") ────────────────────────────────
       Russian and CIS print shops routinely require it in writing — "все шрифты
       должны быть переведены в кривые" — and it is not superstition. An
       embedded font is an instruction the RIP has to execute; an outline is a
       shape. A shop that has been burned by a font that hinted differently, or
       failed to embed, or licensed out, asks for shapes.

       Returns the whole string as ONE SVG path in a y-DOWN coordinate space
       with the baseline at y = 0 — which is what pdf-lib's drawSvgPath consumes
       — plus the advance width in the same units as `size`.

       ⚠️ The path is per-run, not per-glyph, deliberately. Emitting one path
       operator per glyph triples the content stream on a Cyrillic line for no
       visual difference, and some RIPs are measurably slower on it.

       ⚠️ Outlines lose selectability and searchability, which is why this is an
       OPTION and not the default. A card is 30 words; nobody greps it. A
       contract is a different answer, which is why the choice lives with the
       caller. */
    function outline(faceId, text, size, opts) {
        opts = opts || {};
        /* Load the coverage table first. `faceById` reads a table that arrives
           over the network, so a caller that has not happened to call `load()`
           gets "unknown face: montserrat" — which reads like a typo in the face
           id and is actually a race. Doing it here makes the new API safe to
           call cold, which is how the card exporter uses it. */
        return load().then(function () {
            var face = faceById(faceId);
            if (!face) throw new Error('unknown face: ' + faceId);
            var url = (opts.bold && face.boldUrl) ? face.boldUrl : face.url;
            return Promise.all([loadFontkit(), fetchTtf(url)]).then(function (res) {
                return outlineWith(res[0].create(res[1]), text, size, opts);
            });
        });
    }


    /* ── right-to-left: which way each PIECE of a line runs ──────────────────
     *
     * MEASURED, not assumed (2026-08-24). A bench rendered each candidate into
     * a PDF, rasterised it and scored it against the browser drawing the same
     * string with the SAME embedded face. What that showed:
     *
     *   مدرسة النهضة        pdf-lib alone   0.989   ← already perfect
     *   الرياض ٢٠٢٦         pdf-lib alone   0.828   ← «٢٠٢٦» came out «٦٢٠٢»
     *   الرياض ABC 2026     pdf-lib alone   0.781   ← «ABC» came out «CBA»
     *
     * So pdf-lib shapes and orders ARABIC correctly on its own — the earlier
     * reading that «the whole line is backwards» was wrong, and came from
     * comparing against a system font instead of the embedded one. What it gets
     * wrong is narrow: it treats digits and Latin INSIDE an Arabic line as part
     * of that line and reverses them with it. Splitting the line into
     * directional runs and drawing each one separately took those two cases to
     * 0.908 and 0.891, and made them correct to read.
     *
     * Candidates that were tried and scored WORSE, so that nobody re-tries them:
     *   reversing the encoded glyph ids   0.751   (breaks what already worked)
     *   reversing the characters          0.743   (destroys the joining)
     *   our own outline, glyphs reversed  0.748
     *   Unicode bidi isolates in the text 0.885   (fontkit ignores them)
     *
     * This is the short-line subset of UAX#9: first-strong paragraph direction,
     * the weak rules for numbers (W2, W4–W7), N1/N2 for neutrals. Enough for
     * one short line per field — a certificate name, a label caption, a card's
     * address — and honest about being enough for that and not for a paragraph.
     *
     * ⚠️ A NUMBER IS MORE THAN ITS DIGITS (2026-10-07). Until then the pass
     * knew digits and nothing about what sits BETWEEN them, so the «:» of a
     * clock time was a neutral, took the direction of the line, and the two
     * halves of the number swapped around it. Seen on a school timetable
     * (30.09: «7:00 ص» printed «00:7»), then measured against fontkit's own
     * glyph order for every exporter that draws through here:
     *
     *   الساعة 12:30         30:12        الطول 4.54 م     54.4
     *   السعر 1,250.50       50.250,1     12/05/2024       2024/05/12
     *   عنوان 192.168.1.1    1.1.168.192
     *
     * Rule W4 is the whole of it: ONE separator between two numbers of the
     * same kind is part of the number. Not two, and not one with a space
     * beside it — «رقم ١٢٣ / 456» above keeps its slash between two groups.
     * What W4 leaves alone is left alone on purpose, because a browser draws it
     * the same way and the preview must agree with the paper: a range
     * «753–509» and a phone number in groups still read right to left inside
     * Arabic, and «-5» puts its minus on the right.
     *
     * The same measurement found the pass skipping a line with no LETTER in
     * it: «٢٠٢٦» alone in a field printed «٦٢٠٢», because fontkit lays
     * Arabic-Indic digits right to left whether or not a word stands next to
     * them. A line of such digits now takes the same road as any other.
     *
     * ⚠️ IT LIVES HERE, not in a tool, because THREE exporters draw text with
     * the same fontkit layout: certificate-maker, label-maker and
     * business-card. It was written for the first of them, and the other two
     * carried the identical defect for as long as they existed — the Arabic
     * sample the card tool ships with printed its house number ١٢ as ٢١.
     * A rule three callers need is a primitive; a copy in each of them is
     * three chances to fix it once.
     */
    /* ⚠️ ARABIC-INDIC DIGITS LIVE INSIDE THE ARABIC BLOCK (U+0660…0669), so the
     * digit test has to come FIRST. A classifier that asks «is it Arabic?»
     * before «is it a digit?» keeps ٢٠٢٦ inside the word run and changes
     * nothing — which is exactly what the first attempt did, and it scored
     * identically to no fix at all. */
    var RE_AN = /[٠-٩۰-۹]/;                 // Arabic-Indic
    var RE_EN = /[0-9]/;                                        // European
    var RE_R  = /[֐-׿؀-ٟ٪-ۯۺ-ۿ܀-ݏݐ-ݿࢠ-ࣿיִ-﷿ﹰ-﻾]/;
    var RE_L  = /[A-Za-zÀ-ɏͰ-ϿЀ-ӿ぀-ヿ一-鿿가-힯]/;

    /* Arabic LETTERS (with Syriac and Thaana) — the strong type that turns a
     * European number after it into an Arabic number (W2). Hebrew does not. */
    var RE_AL = /[\u0600-\u07bf\u0860-\u086f\u08a0-\u08ff\ufb50-\ufdff\ufe70-\ufefc]/;
    /* What may stand INSIDE a number. Several of these live in the Arabic
     * block (the comma U+060C, the percent sign U+066A, the decimal and
     * thousands separators U+066B/U+066C), so — like the digits above — they
     * are asked about BEFORE «is it Arabic?». */
    var RE_CS = /[,.\/:\u00a0\u060c\u202f\u2044]/;          // common separator
    var RE_ES = /[+\-\u2212]/;                              // European separator
    var RE_ET = /[#$%\u00a2-\u00a5\u00b0\u00b1\u066a\u2030-\u2034\u20a0-\u20cf]/;   // terminator
    var RE_ANSEP = /[\u066b\u066c]/;                        // an Arabic number by class

    function clsOf(c) {
        if (RE_AN.test(c) || RE_ANSEP.test(c)) return 'AN';
        if (RE_EN.test(c)) return 'EN';
        if (RE_CS.test(c)) return 'CS';
        if (RE_ES.test(c)) return 'ES';
        if (RE_ET.test(c)) return 'ET';
        if (RE_R.test(c)) return 'R';
        if (RE_L.test(c)) return 'L';
        return 'N';
    }

    /** Does this line read right to left as a whole? First-strong, UAX#9 P2/P3.
     *  Exported so a PREVIEW can ask the same question the exporter asks: a
     *  canvas defaults to its element's direction, which on an English page is
     *  left-to-right even when the line is Arabic — and then the screen orders
     *  the pieces one way and the paper the other. Measured on the card tool
     *  2026-08-24: «الرياض ABC 2026» drew Arabic-then-Latin on screen and
     *  Latin-then-Arabic on paper. The paper was right. */
    function baseIsRtl(str) {
        var ch = Array.from(String(str == null ? '' : str));
        for (var i = 0; i < ch.length; i++) {
            var c = clsOf(ch[i]);
            if (c === 'R') return true;
            if (c === 'L') return false;
        }
        return false;
    }

    /** [{text, rtl, an}] in VISUAL order, left to right. A line with no
     *  right-to-left letter comes back as one run and nothing downstream
     *  changes — every other language pays nothing for this. */
    function bidiRuns(str) {
        var src = String(str == null ? '' : str);
        var ch = Array.from(src);
        var t = ch.map(clsOf);
        /* ⚠️ «AN» counts, not only «R»: fontkit lays Arabic-Indic digits right
         * to left with or without a word beside them, so «٢٠٢٦» alone in a
         * field needs this pass exactly as much as «الرياض ٢٠٢٦» does. */
        var hasR = false;
        for (var i0 = 0; i0 < t.length; i0++) if (t[i0] === 'R' || t[i0] === 'AN') { hasR = true; break; }
        if (!hasR) return [{ text: src, rtl: false, an: false }];

        /* P2/P3 — the paragraph takes the direction of its first strong. */
        var base = 0;                                   // 0 = ltr, 1 = rtl
        for (var i1 = 0; i1 < t.length; i1++) {
            if (t[i1] === 'R') { base = 1; break; }
            if (t[i1] === 'L') break;
        }

        /* W2 — a European number after an Arabic LETTER is an Arabic number.
         * This is the rule that, in «رقم ١٢٣ / 456», makes the 456 behave like
         * the ١٢٣ beside it. After a Hebrew letter it stays European: the
         * order comes out the same, but W4 and W5 below treat the two kinds
         * differently, so the difference has to survive until then. */
        var strong = base ? 'R' : 'L';
        for (var i2 = 0; i2 < t.length; i2++) {
            if (t[i2] === 'R') strong = RE_AL.test(ch[i2]) ? 'AL' : 'R';
            else if (t[i2] === 'L') strong = 'L';
            else if (t[i2] === 'EN' && strong === 'AL') t[i2] = 'AN';
        }

        /* W4 — ONE separator between two numbers of the same kind belongs to
         * the number: «12:30», «4.54», «1,250.50», «12/05/2024». A plus or a
         * minus joins European numbers only — «5-7» after Arabic letters reads
         * right to left in a browser as well, and the paper must not disagree
         * with the preview. */
        for (var i6 = 1; i6 < t.length - 1; i6++) {
            var before = t[i6 - 1], after = t[i6 + 1];
            if (before !== after) continue;
            if (t[i6] === 'CS' && (before === 'EN' || before === 'AN')) t[i6] = before;
            else if (t[i6] === 'ES' && before === 'EN') t[i6] = 'EN';
        }

        /* W5 — terminators standing against a European number are part of it
         * («50%», «$20»). Against an Arabic number they are not. */
        for (var i7 = 0; i7 < t.length; i7++) {
            if (t[i7] !== 'ET') continue;
            var e7 = i7;
            while (e7 < t.length && t[e7] === 'ET') e7++;
            var joins = (i7 > 0 && t[i7 - 1] === 'EN') || (e7 < t.length && t[e7] === 'EN');
            for (var k7 = i7; k7 < e7; k7++) t[k7] = joins ? 'EN' : 'N';
            i7 = e7 - 1;
        }

        /* W6 — a separator that joined nothing is a neutral like any other. */
        for (var i8 = 0; i8 < t.length; i8++) {
            if (t[i8] === 'CS' || t[i8] === 'ES') t[i8] = 'N';
        }

        /* W7 — a European number after a Latin letter is Latin: this is what
         * keeps «ABC 2026» together. Whatever is still European after that
         * orders like an Arabic number, which is all the rest of the pass
         * needs to know. */
        var lastStrong = base ? 'R' : 'L';
        for (var i9 = 0; i9 < t.length; i9++) {
            if (t[i9] === 'R' || t[i9] === 'L') lastStrong = t[i9];
            else if (t[i9] === 'EN') t[i9] = (lastStrong === 'L') ? 'L' : 'AN';
        }

        /* N1/N2 — neutrals take the surrounding direction when both sides agree,
         * else the paragraph's.
         * ⚠️ NUMBERS COUNT AS RIGHT-TO-LEFT HERE. That is not a quirk, it is
         * what puts the slash in «١٢٣ / 456» between the two groups instead of
         * at the end of the line: treating the numbers as left-to-right made
         * the whole «١٢٣ / 456» one block that moved as a unit, and it came out
         * «رقم 456 / ١٢٣» — the groups swapped. Measured. */
        var side = t.map(function (x) {
            return x === 'N' ? null : (x === 'L' ? 'L' : 'R');
        });
        for (var i3 = 0; i3 < side.length; i3++) {
            if (side[i3]) continue;
            var a = null, b = null;
            for (var j1 = i3 - 1; j1 >= 0; j1--) if (side[j1]) { a = side[j1]; break; }
            for (var j2 = i3 + 1; j2 < side.length; j2++) if (side[j2]) { b = side[j2]; break; }
            side[i3] = (a && a === b) ? a : (base ? 'R' : 'L');
            t[i3] = side[i3] === 'R' ? 'R' : 'L';
        }

        /* Embedding levels. In a right-to-left paragraph Latin text and numbers
         * both sit one level deeper than the Arabic around them, and it is that
         * difference in DEPTH — not a direction flag — that decides how the
         * pieces get ordered. */
        var lvl = t.map(function (x) {
            if (base) return x === 'R' ? 1 : 2;
            return x === 'R' ? 1 : (x === 'AN' ? 2 : 0);
        });

        /* Maximal runs of one level, still in logical order. */
        /* ⚠️ «Arabic number» and «Arabic-Indic digit» are NOT the same thing, and
         * conflating them printed 456 as 654. Rule W2 makes a European digit
         * behave like an Arabic number for ORDERING — but it is still a Latin
         * glyph, and fontkit does not reverse it. The cancelling reversal below
         * must key off the actual characters, not off the ordering class. */
        var runs = [], cur = null;
        for (var i5 = 0; i5 < ch.length; i5++) {
            var isAn = RE_AN.test(ch[i5]);
            if (!cur || cur.lvl !== lvl[i5] || cur.an !== isAn) {
                cur = { text: '', lvl: lvl[i5], an: isAn };
                runs.push(cur);
            }
            cur.text += ch[i5];
        }

        /* L2 — from the deepest level down to the lowest odd one, reverse every
         * contiguous stretch of runs at that level or deeper. Applied to RUNS
         * rather than characters because pdf-lib re-orders the inside of a
         * right-to-left run by itself; doing both would cancel out. */
        var maxLvl = 0;
        runs.forEach(function (r) { if (r.lvl > maxLvl) maxLvl = r.lvl; });
        for (var L = maxLvl; L >= 1; L--) {
            var i = 0;
            while (i < runs.length) {
                if (runs[i].lvl < L) { i++; continue; }
                var j = i;
                while (j < runs.length && runs[j].lvl >= L) j++;
                var part = runs.slice(i, j).reverse();
                for (var k = 0; k < part.length; k++) runs[i + k] = part[k];
                i = j;
            }
        }

        return runs.map(function (r) {
            return { text: r.text, rtl: (r.lvl % 2) === 1, an: r.an };
        });
    }

    /**
     * What to hand pdf-lib for one run.
     *
     * ⚠️ An Arabic-Indic digit is Arabic SCRIPT, so fontkit lays even a
     * digits-only run right to left and ٢٠٢٦ arrives as ٦٢٠٢. Reversing the
     * characters cancels that, and it is safe HERE and only here: digits do not
     * join, so there is no shaping context to destroy — which is precisely why
     * reversing characters was the wrong fix for letters (it scored 0.743).
     */
    /* L4 — a paired sign inside a right-to-left run is drawn as its mirror. fontkit lays the run
       right to left but keeps each glyph as it is, so «إزاحة (أوفست)» printed «إزاحة )أوفست(»
       (measured on the dish installer card, 18.10.2026). The browser does this itself, which is
       why the page looked right and only the paper did not. */
    var MIRROR = { '(': ')', ')': '(', '[': ']', ']': '[', '{': '}', '}': '{', '<': '>', '>': '<', '«': '»', '»': '«', '‹': '›', '›': '‹' };
    function mirrorText(t) {
        return /[()[\]{}<>«»‹›]/.test(t) ? Array.from(t).map(function (c) { return MIRROR[c] || c; }).join('') : t;
    }
    function runText(run) {
        if (!run.rtl && run.an) return Array.from(run.text).reverse().join('');
        return run.rtl ? mirrorText(run.text) : run.text;
    }


    /* ── the same faces, but for the PREVIEW ────────────────────────────────
     *
     * ⚠️ A PREVIEW THAT FALLS BACK TO A SYSTEM FONT IS NOT A PREVIEW. The user
     * picks a typeface for its look; that face is Latin, and the moment the text
     * is Arabic or Korean the canvas quietly borrows whatever the operating
     * system has while the PDF embeds the face `pick()` chose. Nothing warns,
     * both look fine on their own, and the two disagree — measured on the card
     * tool 2026-08-24: the same Arabic line was 14% wider on screen than in
     * print, which is a different line break, a different centring, a different
     * card.
     *
     * So the exporter's fallback ladder is published as a CSS stack. The browser
     * resolves it per character in the same table order `pick()` walks, which is
     * what makes the two agree instead of merely look similar.
     */
    var _cssFaces = {};
    function cssFamily(id, bold) {
        var f = faceById(id);
        if (!f) return null;
        var url = (bold && f.boldUrl) ? f.boldUrl : f.url;
        if (!url || typeof document === 'undefined' || typeof FontFace === 'undefined') return null;
        var fam = 'PTF ' + id + (bold ? ' Bold' : '');
        if (!_cssFaces[fam]) {
            try {
                var ff = new FontFace(fam, 'url(' + url + ')', { weight: bold ? '700' : '400' });
                document.fonts.add(ff);
                _cssFaces[fam] = ff;
            } catch (e) { _cssFaces[fam] = null; }
        }
        return _cssFaces[fam] ? fam : null;
    }

    /** CSS stack of every face the exporter could fall back to, table order. */
    function cssStack(bold) {
        if (!_table) return '';
        var out = [];
        for (var i = 0; i < _table.faces.length; i++) {
            var fam = cssFamily(_table.faces[i].id, bold);
            if (fam) out.push('"' + fam + '"');
        }
        return out.join(', ');
    }

    /** Load exactly the faces `text` will need, then resolve. Canvas does not
     *  trigger a webfont fetch by itself — drawing before this settles paints
     *  the system fallback, which is the very thing this exists to stop. */
    function cssPrepare(text, bold) {
        return ready(text).then(function () {
            var want = {};
            planRuns(String(text || ''), null).forEach(function (r) { if (r.faceId) want[r.faceId] = true; });
            var jobs = Object.keys(want).map(function (id) {
                var fam = cssFamily(id, bold);
                if (!fam || !document.fonts || !document.fonts.load) return null;
                return document.fonts.load('' + (bold ? 700 : 400) + ' 16px "' + fam + '"').catch(function () {});
            }).filter(Boolean);
            return Promise.all(jobs);
        }).catch(function () {});
    }

    /**
     * Runs in VISUAL order, split by DIRECTION first and by COVERAGE second,
     * each run's text already arranged for the shaper. This is what an exporter
     * wants: walk them left to right, draw each with its own face, advance x.
     *
     * ⚠️ THE ORDER OF THE TWO SPLITS IS THE POINT, and getting it backwards
     * is silent. A face split knows nothing about reading order, so doing it
     * first lets one face run straddle a direction boundary — after that there
     * is nothing left to separate, and «رقم ١٢٣ / 456» printed with the slash
     * at the wrong end. It cost a measurement to find; composing the two here
     * means no caller has to know.
     */
    function visualRuns(text, preferId) {
        var out = [];
        bidiRuns(text).forEach(function (b) {
            var faceRuns = planRuns(runText(b), preferId);
            /* Inside a right-to-left piece the face runs are laid from the
               right as well, so their own order flips too. */
            if (b.rtl) faceRuns = faceRuns.slice().reverse();
            faceRuns.forEach(function (r) {
                out.push({ text: r.text, faceId: r.faceId || preferId || null, rtl: b.rtl, an: b.an });
            });
        });
        return out;
    }

    /* Same thing when the caller already holds a fontkit font — the card
       exporter outlines dozens of runs and creating the face once matters. */
    function outlineWith(font, text, size, opts) {
        opts = opts || {};
        var s = size / font.unitsPerEm;
        var track = (opts.track || 0) * size;      // letter-spacing, em → units
        var out = [];
        var x = 0;

        /* ⚠️ ONE `layout()` PER DIRECTIONAL RUN, not one per line. fontkit
         * shapes and orders a right-to-left line correctly on its own, but it
         * has no bidi: digits and Latin sitting INSIDE an Arabic line get
         * reversed along with it. Laying each run out separately and advancing
         * x left to right is the whole fix, and it costs a line with no
         * right-to-left letter nothing — `bidiRuns` hands that back as a single
         * run and the loop below runs exactly once, as it always did. */
        var pieces = bidiRuns(String(text == null ? '' : text));
        var glyphs = [], poss = [];
        pieces.forEach(function (piece) {
            var r = font.layout(runText(piece));
            for (var q = 0; q < r.glyphs.length; q++) { glyphs.push(r.glyphs[q]); poss.push(r.positions[q]); }
        });

        for (var i = 0; i < glyphs.length; i++) {
            var g = glyphs[i];
            var pos = poss[i];
            var cmds = g.path.commands;
            for (var j = 0; j < cmds.length; j++) {
                var c = cmds[j], a = c.args;
                /* fontkit is y-UP (font design space); SVG and pdf-lib's path
                   consumer are y-DOWN. Negating y here is the whole conversion,
                   and doing it anywhere else means two renderers disagree about
                   which way is up — which shows as text mirrored about its own
                   baseline, a failure that looks like a font bug. */
                switch (c.command) {
                    case 'moveTo':
                        out.push('M' + f(x + a[0] * s) + ' ' + f(-a[1] * s)); break;
                    case 'lineTo':
                        out.push('L' + f(x + a[0] * s) + ' ' + f(-a[1] * s)); break;
                    case 'quadraticCurveTo':
                        out.push('Q' + f(x + a[0] * s) + ' ' + f(-a[1] * s) + ' ' +
                                       f(x + a[2] * s) + ' ' + f(-a[3] * s)); break;
                    case 'bezierCurveTo':
                        out.push('C' + f(x + a[0] * s) + ' ' + f(-a[1] * s) + ' ' +
                                       f(x + a[2] * s) + ' ' + f(-a[3] * s) + ' ' +
                                       f(x + a[4] * s) + ' ' + f(-a[5] * s)); break;
                    case 'closePath':
                        out.push('Z'); break;
                }
            }
            x += (pos ? pos.xAdvance : g.advanceWidth) * s + track;
        }
        return { path: out.join(''), width: x, unitsPerEm: font.unitsPerEm };
    }

    /* Three decimals is 1/1000 mm at card scale — far below what a plate can
       hold, and it keeps the content stream from doubling on float noise. */
    function f(v) { return Math.round(v * 1000) / 1000; }

    /* Measure a run WITHOUT drawing it, from the same font that will be used.
       Preview and print then agree because they asked the same object. */
    function advance(font, text, size, opts) {
        opts = opts || {};
        var run = font.layout(String(text == null ? '' : text));
        var s = size / font.unitsPerEm;
        var track = (opts.track || 0) * size;
        var x = 0;
        for (var i = 0; i < run.glyphs.length; i++) {
            var pos = run.positions[i];
            x += (pos ? pos.xAdvance : run.glyphs[i].advanceWidth) * s + track;
        }
        return x;
    }

    /* Hand back a raw fontkit face. Exposed so a caller that outlines many runs
       pays the parse once instead of per run. */
    function face(faceId, opts) {
        opts = opts || {};
        return load().then(function () {          // see `outline` — cold-call safe
            var fc = faceById(faceId);
            if (!fc) throw new Error('unknown face: ' + faceId);
            var url = (opts.bold && fc.boldUrl) ? fc.boldUrl : fc.url;
            return Promise.all([loadFontkit(), fetchTtf(url)]).then(function (res) {
                return { font: res[0].create(res[1]), bytes: res[1], meta: fc };
            });
        });
    }

    /* THE AUTHORITATIVE GATE. The range table is a frozen build artefact; this
       asks the actual font file that is about to be embedded. Use it before
       committing an export, so "the glyph is missing" is something the user
       hears from us rather than discovers in the downloaded file. */
    function verify(faceId, text, opts) {
        opts = opts || {};
        var face = faceById(faceId);
        if (!face) return Promise.reject(new Error('unknown face: ' + faceId));
        var url = (opts.bold && face.boldUrl) ? face.boldUrl : face.url;
        return Promise.all([loadFontkit(), fetchTtf(url)]).then(function (res) {
            var fk = res[0], bytes = res[1];
            var font = fk.create(bytes);
            var set = new Set(font.characterSet);
            var seen = {}, miss = [];
            var cps = codepoints(String(text || ''));
            for (var i = 0; i < cps.length; i++) {
                var cp = cps[i];
                if (seen[cp]) continue;
                seen[cp] = 1;
                if (!set.has(cp)) miss.push(String.fromCodePoint(cp));
            }
            return { ok: miss.length === 0, missing: miss };
        });
    }

    return {
        load: load,
        loadCjk: loadCjk,
        ready: ready,
        needsCjk: needsCjk,
        faces: function () { return _table ? _table.faces.slice() : []; },
        /* ⚠️ `face` is the HEAVY one further down: it downloads the TTF and
           parses it with fontkit. A `face: faceById` used to sit here and was
           silently shadowed by it — a duplicate key in an object literal, last
           one wins, no error anywhere. The cheap metadata lookup lives under a
           name that cannot be confused with it. Ask `faceMeta` when you want to
           know WHERE a face is; ask `face` when you want the outlines. */
        faceMeta: faceById,
        facesFor: facesFor,
        missing: missing,
        canRender: canRender,
        pick: pick,
        planRuns: planRuns,
        bidiRuns: bidiRuns,
        baseIsRtl: baseIsRtl,
        visualRuns: visualRuns,
        cssStack: cssStack,
        cssFamily: cssFamily,
        cssPrepare: cssPrepare,
        runText: runText,
        embed: embed,
        verify: verify,
        /* Text as vector shapes — see the block above `outline` for why RU/CIS
           shops ask for it and why it is not the default. */
        outline: outline,
        outlineWith: outlineWith,
        advance: advance,
        face: face,
        /* Exposed so a test can prove the subsetter's half-byte is gone by
           feeding real font bytes through it — the defect is invisible from
           the outside, so a guard that cannot see the bytes cannot catch it. */
        alignGlyphData: alignGlyphData,
        // test seam — lets node tests inject a table without a network fetch
        __setTable: function (t) { _table = t; _tablePromise = Promise.resolve(t); },
    };
});
